import React from 'react';
import styled, { keyframes } from 'styled-components';

interface CosmicLoadingScreenProps {
  message?: string;
  size?: 'small' | 'medium' | 'large';
  showProgress?: boolean;
  progress?: number;
}

const CosmicLoadingScreen: React.FC<CosmicLoadingScreenProps> = ({
  message = 'Loading...',
  size = 'medium',
  showProgress = false,
  progress = 0,
}) => {
  return (
    <LoadingContainer>
      <CosmicBackground />
      <LoadingContent>
        <SpinnerContainer $size={size}>
          <OrbitingRing>
            <Planet />
          </OrbitingRing>
          <InnerGlow />
        </SpinnerContainer>
        
        <LoadingMessage>{message}</LoadingMessage>
        
        {showProgress && (
          <ProgressContainer>
            <ProgressBar>
              <ProgressFill style={{ width: `${progress}%` }} />
            </ProgressBar>
            <ProgressText>{progress}%</ProgressText>
          </ProgressContainer>
        )}
        
        <FloatingStars>
          <Star style={{ '--delay': '0s', '--x': '10%', '--y': '20%' } as React.CSSProperties}>✨</Star>
          <Star style={{ '--delay': '1s', '--x': '80%', '--y': '10%' } as React.CSSProperties}>⭐</Star>
          <Star style={{ '--delay': '2s', '--x': '60%', '--y': '80%' } as React.CSSProperties}>🌟</Star>
          <Star style={{ '--delay': '3s', '--x': '30%', '--y': '70%' } as React.CSSProperties}>💫</Star>
        </FloatingStars>
      </LoadingContent>
    </LoadingContainer>
  );
};

// Animations
const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const orbit = keyframes`
  from { transform: rotate(0deg) translateX(40px) rotate(0deg); }
  to { transform: rotate(360deg) translateX(40px) rotate(-360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.1); }
`;

const twinkle = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.2); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(147, 112, 219, 0.5); }
  50% { box-shadow: 0 0 40px rgba(147, 112, 219, 0.8), 0 0 60px rgba(147, 112, 219, 0.4); }
`;

// Styled Components
const LoadingContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 40%, #0f0f23 100%);
  z-index: 9999;
  overflow: hidden;
`;

const CosmicBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 30%, rgba(147, 112, 219, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(65, 105, 225, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 60% 20%, rgba(0, 206, 209, 0.05) 0%, transparent 50%);
`;

const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  position: relative;
  z-index: 1;
`;

const SpinnerContainer = styled.div<{ $size: 'small' | 'medium' | 'large' }>`
  position: relative;
  width: ${props => {
    switch (props.$size) {
      case 'small': return '60px';
      case 'large': return '120px';
      default: return '80px';
    }
  }};
  height: ${props => {
    switch (props.$size) {
      case 'small': return '60px';
      case 'large': return '120px';
      default: return '80px';
    }
  }};
`;

const OrbitingRing = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 80px;
  height: 80px;
  margin: -40px 0 0 -40px;
  border: 2px solid transparent;
  border-top: 2px solid #9370db;
  border-right: 2px solid #4169e1;
  border-radius: 50%;
  animation: ${rotate} 2s linear infinite;
`;

const Planet = styled.div`
  position: absolute;
  top: -6px;
  left: 50%;
  width: 12px;
  height: 12px;
  margin-left: -6px;
  background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(255, 215, 0, 0.6);
  animation: ${orbit} 3s linear infinite;
`;

const InnerGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 40px;
  height: 40px;
  margin: -20px 0 0 -20px;
  background: radial-gradient(circle, rgba(147, 112, 219, 0.3) 0%, transparent 70%);
  border-radius: 50%;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const LoadingMessage = styled.p`
  color: #e0e7ff;
  font-size: 1.125rem;
  font-weight: 500;
  margin: 0;
  text-align: center;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  letter-spacing: 0.025em;
`;

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 200px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #9370db 0%, #4169e1 50%, #00ced1 100%);
  border-radius: 2px;
  transition: width 0.3s ease;
  box-shadow: ${glow};
`;

const ProgressText = styled.span`
  color: #94a3b8;
  font-size: 0.875rem;
  font-weight: 500;
`;

const FloatingStars = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

const Star = styled.span`
  position: absolute;
  font-size: 16px;
  animation: ${twinkle} 3s ease-in-out infinite;
  animation-delay: var(--delay);
  left: var(--x);
  top: var(--y);
`;

export default CosmicLoadingScreen;